import React, { useContext } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import colors from "core/colors";

import BodyText from "core/BodyText";
import CheckIcon from "core/ReusableIcons/Checkmark";

const RadioAccordionContext = React.createContext();

const RadioGroup = styled.div`
    color: ${colors.primary.diamond};
    border: 1px solid ${colors.secondary.dolphin};
    border-radius: 4px;
`;

const ItemContainer = styled.div`
    display: flex;
    flex-grow: 1;
    gap: 1rem;
    border-top: 1px solid ${colors.secondary.dolphin};
    &:first-child {
        border-top: none;
    }
    padding: 1rem;
    background-color: ${props =>
        props["aria-checked"] ? colors.secondary.oolong : "transparent"};
    &:first-child {
        border-radius: 4px 4px 0 0;
    }
    &:last-child {
        border-radius: 0 0 4px 4px;
    }
`;

export const BuyBoxItemLabel = styled.label`
    display: flex;
    justify-content: space-between;
    font-size: 1rem;
    line-height: 1.25rem;
    gap: 0.5rem;
`;

export const BuyBoxItemList = styled(BodyText).attrs({
    type: "ul",
    size: "xs",
    copyColor: colors.primary.diamond
})`
    list-style-type: disc;
    padding-left: 1rem;
    margin-top: 0.25rem;
    line-height: 1.15rem;
`;

const SelectIndicator = styled.span`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 50%;
    background-color: ${({ selected }) =>
        selected ? colors.accent.darkMode.suenosBuenos : "transparent"};
    border: 2px solid
        ${({ selected }) =>
            selected
                ? colors.accent.darkMode.suenosBuenos
                : colors.secondary.dolphin};
    svg {
        width: 0.9rem;
        height: 0.9rem;
    }
`;

export const BuyBoxRadioGroup = ({ selected, onSelect, children }) => {
    return (
        <RadioAccordionContext.Provider value={{ selected, onSelect }}>
            <RadioGroup role="radiogroup">{children}</RadioGroup>
        </RadioAccordionContext.Provider>
    );
};

BuyBoxRadioGroup.propTypes = {
    selected: PropTypes.string.isRequired,
    onSelect: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired
};

export const BuyBoxRadioItem = ({ name, children }) => {
    const { selected, onSelect } = useContext(RadioAccordionContext);
    const isSelected = selected === name;

    return (
        <ItemContainer
            role="radio"
            aria-checked={isSelected}
            value={name}
            tabIndex="0"
            onClick={() => onSelect(name)}
            onKeyPress={e => {
                if (e.key === "Enter") {
                    onSelect(name);
                }
            }}
        >
            <div>
                <SelectIndicator selected={isSelected}>
                    {isSelected ? <CheckIcon /> : null}
                </SelectIndicator>
            </div>
            <div style={{ flexGrow: 1 }}>{children}</div>
        </ItemContainer>
    );
};

BuyBoxRadioItem.propTypes = {
    name: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired
};
